'use client'

import { useEffect, useRef, ReactNode } from 'react'

interface FaqAutoCloseProps {
  children: ReactNode
  enabled?: boolean
}

/**
 * Client component that adds autoclose behavior to FAQ sections.
 * Preserves SSR/SEO by enhancing native <details> elements.
 */
export function FaqAutoClose({ children, enabled = true }: FaqAutoCloseProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enabled || !containerRef.current) return

    const container = containerRef.current
    const detailsElements = container.querySelectorAll('details')

    const handleToggle = (event: Event) => {
      const openedDetails = event.target as HTMLDetailsElement
      if (!openedDetails.open) return

      // Close all other details in this container
      detailsElements.forEach((details) => {
        if (details !== openedDetails && details.open) {
          details.open = false
        }
      })
    }

    detailsElements.forEach((details) => {
      details.addEventListener('toggle', handleToggle)
    })

    return () => {
      detailsElements.forEach((details) => {
        details.removeEventListener('toggle', handleToggle)
      })
    }
  }, [enabled])

  return <div ref={containerRef}>{children}</div>
}
